
import { Invoices, Quotes } from "@/api";
import { OfficeType } from "@/api/types";
import Card from "@/components/common/Card.vue";
import GoogleAddressAutocomplete from "@/components/common/GoogleAddressAutocomplete.vue";
import PageTitle from "@/components/common/PageTitle.vue";
import DelegatesList from "@/components/delegates/DelegatesList.vue";
import CompanyPrice from "@/components/events/CompanyPrice.vue";
import BookingContactFields from "@/components/events/BookingContactFields.vue";
import EventExpenseSetup from "@/components/events/EventExpenseSetup.vue";
import Spinner from "@/components/shared/Spinner.vue";
import {
  DistancePoint,
  GooglePlace,
  getDrivingDistance,
  metersToKm
} from "@/utils/googleMaps";
import {
  DEFAULT_OFF_SITE_RATES,
  OffSiteRates,
  mapOffSiteRates
} from "@/utils/offSiteRates";
import {
  EVENT_TYPE_AUDIT,
  EVENT_TYPE_ONLINE_COURSE,
  OFFICE_TYPE_ONLINE
} from "@/utils/constants";
import { quoteBookingCompanies } from "@/utils/quoteCourses";
import { calculateCustomerTransportCharge } from "@/utils/transport";
import moment from "moment";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { Component, Vue, Watch } from "vue-property-decorator";

import { namespace } from "vuex-class";

const Auth = namespace("auth");
const Events = namespace("events");
const Company = namespace("company");
const Instructors = namespace("instructors");

@Component({
  components: {
    DelegatesList,
    Card,
    PageTitle,
    ValidationObserver,
    ValidationProvider,
    CompanyPrice,
    BookingContactFields,
    EventExpenseSetup,
    Spinner,
    GoogleAddressAutocomplete
  }
})
export default class CreateEventPage extends Vue {
  OCCUPATIONAL_QUALIFICATION_ID = 8;

  currentDate = moment().format("YYYY-MM-DD");
  nextDay = moment()
    .add(1, "days")
    .format("YYYY-MM-DD");
  pastDate = moment()
    .subtract(366, "days")
    .format("YYYY-MM-DD");
  selectedDelegates: any[] = [];
  bookingTypes = [];
  quotes: any[] = [];
  quoteSearch = "";
  quoteLoading = false;
  quoteRequestId = 0;
  private quoteSearchTimer: number | null = null;
  courseDurationId = 1;
  courseOverview: Array<{ title: string; content: any }> = [];
  isSingleSelect = false;
  addressPrepopulate = "";
  distanceText = "";
  distanceLoading = false;
  offSiteRates: OffSiteRates = { ...DEFAULT_OFF_SITE_RATES };
  tableHeader = {
    title: "Assign delegates",
    icon: "mdi-account"
  };
  menu2 = false;
  menu3 = false;
  menu4 = false;
  menu = false;
  buttonsArray = [
    {
      text: "Back",
      icon: "mdi-chevron-left",
      color: "white",
      action: "back",
      disabled: false
    },
    {
      text: "Save",
      icon: "mdi-pencil-box-outline",
      color: "dark",
      action: "save",
      disabled: true
    }
  ];
  durationVal: any = 0;
  selectedCourseCatId: any = null;
  headersTitle = [{ icon: "mdi-file-edit", header: "Booking Request" }];
  loading = false;

  @Auth.Getter("getUser")
  getUser!: object | null;
  @Events.Action("getCoursesList")
  private getCoursesList!: () => Promise<any>;
  @Events.Action("getAuditsList")
  private getAuditsList!: () => Promise<any>;
  @Events.Action("getOnlineCoursesList")
  private getOnlineCoursesList!: () => Promise<any>;
  @Events.Action("getEventTypes")
  private getEventTypes!: () => Promise<any>;
  @Events.Action("getOfficeAddress")
  private getOfficeAddress!: () => Promise<any>;
  @Events.Action("getOfficeTypes")
  private getOfficeTypes!: () => Promise<any>;
  @Events.Action("getCoursesDurations")
  private getCoursesDurations!: () => Promise<any>;
  @Events.Action("getCoursesInfo")
  private getCoursesInfo!: (id: string) => Promise<any>;
  @Events.Action("getAuditInfo")
  private getAuditInfo!: (id: string) => Promise<any>;
  @Events.Action("getOnlineCoursesInfo")
  private getOnlineCoursesInfo!: (id: string) => Promise<any>;
  @Events.Action("createEvent")
  createEvent!: (params: any) => Promise<void>;
  @Instructors.Action("getInstructorslistByCourseID")
  private getInstructorslistByCourseID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Instructors.Action("getInstructorslistByAuditID")
  private getInstructorslistByAuditID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Instructors.Action("getInstructorslistByOnlineCourseID")
  private getInstructorslistByOnlineCourseID!: ({
    id,
    active
  }: {
    id: number;
    active: string | number;
  }) => Promise<any>;
  @Company.Action("getListCompanyWithPagination")
  private getListCompanyWithPagination!: () => Promise<any>;

  @Events.Mutation("startFetch")
  eventStartFetch!: () => void;
  @Events.Mutation("setEventType")
  setEventType!: (type: any) => void;
  @Events.Mutation("setEventEventableId")
  setEventEventableId!: () => void;
  @Company.Mutation("startFetch")
  companyStartFetch!: () => void;
  @Instructors.Mutation("setInstructors")
  setInstructors!: (payload: []) => void;

  @Events.State("courses") courses: any;
  @Events.State("audits") audits: any;
  @Events.State("onlineCourses") onlineCourses: any;
  @Events.State("officeTypes") officeTypes: any;
  @Events.State("event") event: any;
  @Events.State("durations") durations: any;
  @Events.State("coursesInfo") coursesInfo: any;
  @Instructors.State("instructors") instructors: any;

  get isTransportDisabled() {
    return !this.addressRules;
  }
  get fieldsLocked(): boolean {
    return (
      !this.event.eventType?.id ||
      !this.event.eventableId ||
      !this.event.quoteIds?.length
    );
  }
  get availableCourses(): any[] {
    return this.courses || [];
  }
  get eventableItems(): any[] {
    if (this.event.eventType?.name === "Audit") {
      return this.audits || [];
    }
    if (this.event.eventType?.name === "Online Course") {
      return this.onlineCourses || [];
    }
    return this.availableCourses;
  }
  get eventableLabel(): string {
    if (this.event.eventType?.name === "Audit") {
      return "Select Audit";
    }
    if (this.event.eventType?.name === "Online Course") {
      return "Select Online Course";
    }
    return "Select Course";
  }
  /**
   * Where the booking can be delivered. An online course is only ever
   * online. A training course may be at our office, the customer's premise
   * or online. An audit is the customer's or ours and never online, so the
   * Online premise is withheld from it rather than merely discouraged.
   */
  get premiseOptions(): OfficeType[] {
    const eventTypeId = Number(this.event.eventType?.id);
    return (this.officeTypes || []).filter((officeType: OfficeType) => {
      const isOnline = Number(officeType.id) === OFFICE_TYPE_ONLINE;
      if (eventTypeId === EVENT_TYPE_ONLINE_COURSE) {
        return isOnline;
      }
      if (eventTypeId === EVENT_TYPE_AUDIT) {
        return !isOnline;
      }
      return true;
    });
  }

  get isCustomerPremise(): boolean {
    return Number(this.event.officeTypeId) === 2;
  }
  get addressRules() {
    const index = this.officeTypes.findIndex(
      (officeType: OfficeType) => officeType.id === this.event.officeTypeId
    );
    if (index !== -1) {
      const selectedOfficeType: OfficeType = this.officeTypes[index];
      if (selectedOfficeType) {
        return selectedOfficeType.validateAddress ? "required" : "";
      }
    }
    return "";
  }
  get checkUser() {
    const currentUser: any = this.getUser;
    const isManager =
      currentUser && currentUser.permissions
        ? Boolean(
            currentUser.permissions.find(
              (permission: string) => permission === "create_role"
            )
          )
        : false;
    return isManager;
  }
  get minStartDate() {
    const currentUser: any = this.getUser;
    const isPermitedPast = Boolean(
      currentUser.permissions.find(
        (permission: string) => permission === "create_past_event"
      )
    );
    const retDate = this.checkUser ? this.currentDate : this.nextDay;
    return isPermitedPast ? this.pastDate : retDate;
  }
  get isOccupationalQualification() {
    return this.coursesInfo?.category?.id == this.OCCUPATIONAL_QUALIFICATION_ID;
  }

  async created() {
    this.loading = true;
    try {
      this.eventStartFetch();
      this.companyStartFetch();
      await this.loadOffSiteRates();
      await this.getCoursesList();
      await this.getOfficeTypes();
      await this.getCoursesDurations();
      await this.getListCompanyWithPagination();
      this.bookingTypes = await this.getEventTypes();
      this.setEventType(this.bookingTypes[0]);
      const loadedAddress = await this.getOfficeAddress();
      if (loadedAddress.data.length > 0) {
        this.addressPrepopulate = loadedAddress.data[0].address;
        this.event.address = this.addressPrepopulate;
      }
    } catch (e) {
      await this.$error(e.errors || { err: e.result.message });
    }
    if (this.$route.params.date) {
      this.event.eventStartDate = moment(this.$route.params.date).format(
        "YYYY-MM-DD"
      );
    }
    // Transport is formula-driven & read-only; seed it (0 until a venue is picked).
    this.recalculateTransport();
    this.loading = false;
  }

  async loadOffSiteRates(): Promise<void> {
    try {
      this.offSiteRates = mapOffSiteRates(await Invoices.rates());
    } catch (e) {
      this.offSiteRates = { ...DEFAULT_OFF_SITE_RATES };
    }
  }

  @Watch("event.eventType")
  async onSelectBookingType(type: any) {
    if (type) {
      this.setEventEventableId();
      this.event.instructorId = null;
      this.setInstructors([]);
      this.courseOverview = [];
      this.selectedCourseCatId = null;
      this.selectedDelegates = [];
      this.event.quoteId = null;
      this.event.quoteIds = [];
      this.quotes = [];
      this.event.company = [];
      if (type.name === "Audit") {
        await this.getAuditsList().catch(e => {
          this.$error({ error: e });
        });
      }
      if (type.name === "Online Course") {
        await this.getOnlineCoursesList().catch(e => {
          this.$error({ error: e });
        });
      }

      // An online course is delivered online and nowhere else; a training
      // course or an audit may be held at our office, on the customer's
      // premise or online, so those keep whatever premise is picked.
      if (type.id === 3) this.event.officeTypeId = 3;
    }
  }
  @Watch("event.eventableId")
  async onSelectCourse(id: number, oldId: number) {
    this.setInstructors([]);
    if (id && this.event.eventType.name === "Course") {
      try {
        if (Number(id) !== Number(oldId)) {
          this.event.quoteId = null;
          this.event.quoteIds = [];
          this.event.company = [];
          this.quotes = [];
        }
        await this.getInstructorslistByCourseID({ id, active: 1 });
        await this.getCoursesInfo(id.toString());
        const course = this.coursesInfo;
        this.selectedCourseCatId = course.category.id;
        this.courseDurationId = course.duration.id;
        const durationName =
          this.coursesInfo.category.id === 3 ? "" : course.duration;

        this.courseOverview = [
          { title: "Name", content: course.name },
          {
            title: "Duration",
            content: durationName.name
          },
          { title: "Certificate", content: course.courseType },
          {
            title: "Catalogue Cost Per Day",
            content: `${new Intl.NumberFormat("en", {
              style: "currency",
              currency: "ZAR",
              minimumFractionDigits: 2
            })
              .format(this.coursesInfo.price || 0)
              .substr(2)}`
          },
          {
            title: course.dol ? "DOL #" : "",
            content: course.dol ? course.dol : ""
          }
        ];
        if (this.coursesInfo.category.id === 3) {
          this.durationVal = course.duration;
        }
      } catch (e) {
        await this.$error(e);
      }
    }

    if (id && this.event.eventType.name === "Audit") {
      try {
        await this.getInstructorslistByAuditID({ id, active: 1 });
        await this.getAuditInfo(id.toString());
        this.courseDurationId = this.coursesInfo.duration;
      } catch (e) {
        await this.$error(e);
      }
    }

    if (id && this.event.eventType.name === "Online Course") {
      try {
        await this.getInstructorslistByOnlineCourseID({ id, active: 1 });
        await this.getOnlineCoursesInfo(id.toString());

        const course = this.coursesInfo;

        this.courseDurationId = course.duration.id;
        const durationName =
          this.coursesInfo.category.id === 3 ? "" : course.duration;
        this.courseOverview = [
          { title: "Name", content: course.name },
          {
            title: "Duration",
            content: durationName.name
          },
          { title: "Certificate", content: course.type.name },
          {
            title: "Catalogue Cost Per Day",
            content: `${new Intl.NumberFormat("en", {
              style: "currency",
              currency: "ZAR",
              minimumFractionDigits: 2
            })
              .format(this.coursesInfo.price || 0)
              .substr(2)}`
          },
          {
            title: "DOL #",
            content: course.dol != "null" ? course.dol : "- "
          }
        ];
        if (this.coursesInfo.category.id === 3) {
          this.durationVal = course.duration;
        }
      } catch (e) {
        await this.$error(e);
      }
    }
    if (id) {
      await this.loadQuotes();
      this.applySelectedQuotes();
    }
    this.setSaveDisabled(this.fieldsLocked);
    // Course (and its days) changed -> refresh Transport.
    this.recalculateTransport();
  }
  @Watch("event.officeTypeId")
  async onOfficeTypeChange(value: any) {
    if (value === 1) {
      // Venue is the office itself — no travel, reset venue place/distance.
      this.event.address = this.addressPrepopulate;
      this.event.placeId = null;
      this.event.distance = null;
      this.distanceText = "";
      this.resetAdditionalCosts();
    } else {
      this.event.address = "";
      this.event.placeId = null;
      this.event.distance = null;
      this.distanceText = "";
    }
    this.recalculateTransport();
  }

  onVenueSelect(place: GooglePlace): void {
    this.event.address = place.address;
    this.event.placeId = place.placeId;
    this.computeDistance();
  }

  /**
   * The chosen instructor, but only when their profile says to measure travel
   * from where they live and an address is actually on file. One saved before
   * that address became mandatory falls back to the office.
   */
  get instructorTravellingFromHome(): any | null {
    if (!this.event.instructorId) {
      return null;
    }
    const instructor = (this.instructors || []).find(
      (item: any) => Number(item.id) === Number(this.event.instructorId)
    );
    return instructor &&
      instructor.calculate_travel_from_home &&
      instructor.home_address
      ? instructor
      : null;
  }

  /** Travel is measured from the company office unless the above says otherwise. */
  get travelOrigin(): DistancePoint {
    const instructor = this.instructorTravellingFromHome;
    return instructor
      ? {
          placeId: instructor.home_place_id || null,
          address: instructor.home_address
        }
      : { address: this.addressPrepopulate };
  }

  get isTravelFromInstructorHome(): boolean {
    return this.instructorTravellingFromHome !== null;
  }

  get distanceLabel(): string {
    return this.isTravelFromInstructorHome
      ? "Distance from instructor's home"
      : "Distance from office";
  }

  // Swapping the instructor can swap the origin, so the distance (and the
  // transport charge derived from it) has to be recomputed.
  @Watch("event.instructorId")
  onInstructorChangeForTravel(): void {
    this.computeDistance();
  }

  async computeDistance(): Promise<void> {
    // Distance is travel origin -> course venue (reference value, LMS-1236).
    const origin = this.travelOrigin;
    if (!origin.address || !this.event.address) {
      this.event.distance = null;
      this.distanceText = "";
      return;
    }
    this.distanceLoading = true;
    try {
      const result = await getDrivingDistance(origin, {
        placeId: this.event.placeId,
        address: this.event.address
      });
      if (result) {
        this.event.distance = result.meters;
        this.distanceText = metersToKm(result.meters);
      } else {
        this.event.distance = null;
        this.distanceText = "";
      }
    } catch (e) {
      this.event.distance = null;
      this.distanceText = "";
    } finally {
      this.distanceLoading = false;
      this.recalculateTransport();
    }
  }

  // Course duration in days — drives the Transport formula (LMS-1236).
  get durationDays(): number {
    if (this.selectedCourseCatId === 3 && this.event.duration) {
      const match = (this.durations || []).find(
        (d: any) => d.id === this.event.duration
      );
      // Duration names look like "5-day" -> parseInt extracts the leading number.
      const days = match ? parseInt(match.name, 10) : 0;
      return days > 0 ? days : 1;
    }
    const duration: any = this.coursesInfo && this.coursesInfo.duration;
    if (duration) {
      const days =
        typeof duration === "object"
          ? parseInt(duration.name, 10)
          : parseInt(duration, 10);
      return days > 0 ? days : 1;
    }
    return 1;
  }

  onAccommodationToggle(value: boolean): void {
    this.event.accommodationIncluded = value;
    if (value) {
      this.event.accommodationCheckIn =
        this.event.accommodationCheckIn || this.event.eventStartDate;
      this.event.accommodationCheckOut =
        this.event.accommodationCheckOut ||
        moment(this.event.eventStartDate)
          .add(this.durationDays - 1, "days")
          .format("YYYY-MM-DD");
    }
    // No accommodation -> no accommodation cost.
    if (!value) {
      this.event.accommodation = 0;
      this.event.accommodationName = "";
      this.event.accommodationAddress = "";
      this.event.accommodationCheckIn = "";
      this.event.accommodationCheckOut = "";
    }
    this.recalculateTransport();
  }

  resetAdditionalCosts(): void {
    this.event.accommodationIncluded = false;
    this.event.accommodation = 0;
    this.event.accommodationName = "";
    this.event.accommodationAddress = "";
    this.event.accommodationCheckIn = "";
    this.event.accommodationCheckOut = "";
    this.event.claimFuelAmount = 0;
    this.event.claimSntAmount = 0;
    this.event.claimSntIncluded = false;
    this.event.claimBreakfastIncluded = false;
    this.event.claimDinnerIncluded = false;
    this.event.claimSntQty = 0;
    this.event.claimBreakfastQty = 0;
    this.event.claimDinnerQty = 0;
    this.event.claimBreakfastAmount = 0;
    this.event.claimDinnerAmount = 0;
    this.event.claimAccommodationAmount = 0;
    this.event.claimTollsAmount = 0;
    this.event.claimOtherAmount = 0;
    this.event.claimOtherDescription = "";
    this.event.expenseNotes = "";
  }

  // Customer charge = return distance for every training day, or one return
  // trip when accommodation is included.
  recalculateTransport(): void {
    this.event.transportPrice = calculateCustomerTransportCharge(
      this.event.distance,
      this.durationDays,
      Boolean(this.event.accommodationIncluded),
      this.offSiteRates.customerTransportPerKm
    ).toFixed(2);
  }

  // Quote dropdown label — just the id, e.g. "#1234".
  quoteLabel(quote: any): string {
    if (!quote) {
      return "";
    }
    // A quote already booked for this item stays in the list, greyed out, so
    // the agent can see it exists rather than wonder where it went.
    const suffix = quote.alreadyBooked ? " — already booked" : "";
    return `#${quote.id} — ${quote.companyName}${suffix}`;
  }

  quoteDisabled(quote: any): boolean {
    return Boolean(quote && quote.alreadyBooked);
  }

  /**
   * The quote list only offers quotes that carry the exact item being booked,
   * and a course and an online course can share a name to the letter. Saying
   * so beats an empty dropdown with no explanation.
   */
  get noQuotesHint(): string {
    if (!this.event.eventableId) {
      return "Choose what is being booked first.";
    }
    const kind = (this.event.eventType?.name || "item").toLowerCase();
    return `No quote lists this ${kind}. Raise or amend a quote so it includes this exact ${kind} — a quote for the classroom course does not cover the online one, or the other way round.`;
  }

  async loadQuotes(search = ""): Promise<void> {
    const requestId = ++this.quoteRequestId;
    const bookingType = this.event.eventType?.name;
    const eventableId = this.event.eventableId;
    if (!this.event.eventType?.id || !this.event.eventableId) {
      this.quotes = [];
      this.quoteLoading = false;
      return;
    }

    this.quoteLoading = true;
    try {
      const filters: any = { limit: 1000 };
      // Courses, online courses and audits are all quoted the same way, so
      // every booking offers only the quotes that carry what it is booking.
      filters.bookingCourseId = this.event.eventableId;
      filters.bookingEventableType = this.event.eventType.eventableType;
      if (search) {
        filters.name = search;
      }
      const response: any = await Quotes.loadQuotesData(1, filters);
      if (
        requestId !== this.quoteRequestId ||
        bookingType !== this.event.eventType?.name ||
        eventableId !== this.event.eventableId
      )
        return;
      const selectedQuotes = this.quotes.filter((quote: any) =>
        (this.event.quoteIds || []).includes(quote.id)
      );
      const loadedQuotes = [...selectedQuotes, ...(response.data || [])];
      const missingIds = (this.event.quoteIds || []).filter(
        (id: number) => !loadedQuotes.some(quote => quote.id === id)
      );
      const missingQuotes = await Promise.all(
        missingIds.map((id: number) => Quotes.fetchQuoteById(id))
      );
      if (
        requestId !== this.quoteRequestId ||
        bookingType !== this.event.eventType?.name ||
        eventableId !== this.event.eventableId
      )
        return;
      this.quotes = [...loadedQuotes, ...missingQuotes].filter(
        (quote: any, index: number, values: any[]) =>
          values.findIndex(item => item.id === quote.id) === index
      );
    } catch (e) {
      if (requestId === this.quoteRequestId) await this.$error(e);
    } finally {
      if (requestId === this.quoteRequestId) this.quoteLoading = false;
    }
  }

  @Watch("quoteSearch")
  onQuoteSearch(value: string): void {
    if (this.quoteSearchTimer !== null) {
      window.clearTimeout(this.quoteSearchTimer);
    }
    this.quoteSearchTimer = window.setTimeout(() => {
      this.loadQuotes(value || "");
    }, 500);
  }

  onQuotesSelect(quoteIds: number[] = []): void {
    this.event.quoteIds = quoteIds || [];
    this.event.quoteId = this.event.quoteIds[0] || null;
    this.applySelectedQuotes();
    this.setSaveDisabled(this.fieldsLocked);
  }

  applySelectedQuotes(): void {
    const selected: any[] = (this.event.quoteIds || []).map((id: number) =>
      this.quotes.find(quote => quote.id === id)
    );
    // Do not erase saved companies if a selected quote could not be loaded.
    if (selected.some(quote => !quote)) return;
    this.event.company = quoteBookingCompanies(
      selected,
      this.event.company || [],
      this.event.eventType.name,
      this.event.eventableId,
      this.coursesInfo?.price
    );
  }

  setSaveDisabled(disabled: boolean): void {
    const saveButton = this.buttonsArray.find(
      button => button.action === "save"
    );
    if (saveButton) {
      saveButton.disabled = disabled;
    }
  }

  @Watch("event.company", { deep: true })
  editSumCard() {
    if (this.event.company.length) {
      this.updPrice();
    }
    const a = this.courseOverview;
    if (a && a[3]?.content) {
      a[3].content = `${new Intl.NumberFormat("en", {
        style: "currency",
        currency: "ZAR",
        minimumFractionDigits: 2
      })
        .format(this.coursesInfo.price)
        .substr(2)}`;
    }
  }

  @Watch("selectedDelegates")
  updAssign() {
    this.updPrice();
    const a = this.courseOverview;
    if (a && a[3]?.content) {
      a[3].content = `${new Intl.NumberFormat("en", {
        style: "currency",
        currency: "ZAR",
        minimumFractionDigits: 2
      })
        .format(this.coursesInfo.price)
        .substr(2)}`;
    }
  }

  updPrice() {
    let sum = 0;
    this.selectedDelegates.forEach((d: any) => {
      this.event.company?.forEach((c: any) => {
        if (d.companyId === c.companyId) {
          sum += +c.price;
        }
      });
    });
    this.event.price = sum / (this.selectedDelegates.length || 1);
  }

  get formattedEventStartDate() {
    return moment(this.event.eventStartDate, "YYYY-MM-DD").format("DD/MM/YYYY");
  }
  formattedEventEndDate() {
    return moment(this.event.eventFinishDate).format("DD/MM/YYYY");
  }

  onDurationSelect() {
    const localDurations: any = this.durations;
    this.courseOverview[1].content = localDurations.find(
      (duration: any) => duration.id === this.event.duration
    ).name;
    // Days changed -> Transport may change when no accommodation.
    this.recalculateTransport();
  }

  async onGoBack() {
    await this.$router.push({ name: "CourseEvents" });
  }
  async onSave() {
    (this.$refs.form as Vue & {
      click: () => void;
    }).click();
  }
  async save() {
    try {
      this.buttonsArray.forEach(btn => {
        btn.disabled = true;
      });
      if (this.event.instructorId === null) {
        this.event.instructorId = "";
      }
      this.event.transportPrice += "";
      const eventData = JSON.parse(JSON.stringify(this.event));
      // Tollgate information is added only after the booking exists by the
      // role that holds the edit-only tollgate permission.
      delete eventData.claimTollsAmount;
      eventData.eventStartDate = moment(this.event.eventStartDate).format(
        "DD-MM-YYYY"
      );

      eventData.eventableType = this.event.eventType.eventableType;
      eventData.courseDurationId = this.courseDurationId;
      eventData.eventTypeId = this.event.eventType.id;

      if (this.selectedCourseCatId !== 3) {
        delete eventData.duration;
      } else {
        eventData.courseDurationId = eventData.duration;
        delete eventData.duration;
      }
      // eventData.courseDurationId = this.coursesInfo.durationDays || 1;
      // if (this.event.eventTypeId == 2) eventData.courseId = eventData.auditId;
      if (!this.event.price) eventData.price = this.coursesInfo.price;
      if (this.event.eventType.id == 3) eventData.officeTypeId = 3;

      const newEvent: any = await this.createEvent({
        eventData,
        selectedDelegates: this.selectedDelegates
      });
      await this.$success("<strong>Success!</strong> Event has been created!");

      if (this.isOccupationalQualification) {
        const res = await this.$dialog.error({
          text:
            "Do you want to create an Offline Training Schedule For Qualifications. You can do this late on the event edit page",
          title: "Warning",
          persistent: true,
          actions: {
            No: { text: "No" },
            Yes: {
              color: "black",
              text: "Yes I do"
            }
          }
        });
        if (res === "Yes") {
          this.$router.push(`/event/${newEvent.id}/edit`);
        }
      } else if (this.$route.name === "CourseEventsCreate") {
        await this.$router.push({ name: "CourseEvents" });
      }
    } catch (e) {
      (this.$refs.eventForm as Vue & {
        setErrors: (errors: any) => void;
      }).setErrors(e);
      await this.$error(e);
    } finally {
      this.buttonsArray.forEach(btn => {
        btn.disabled = btn.action === "save" ? this.fieldsLocked : false;
      });
    }
  }
  getCompaniesGoal(data: any) {
    this.event.company = data;
  }
  async handleCourseChange() {
    const eventData: any = this.event;
    await this.getCoursesInfo(eventData.eventableId.toString());
    this.selectedCourseCatId = this.coursesInfo.category.id;
    this.applySelectedQuotes();
  }
  removeAssign(company: number) {
    const newDelegates: { id: number; companyId: number }[] = [];
    this.selectedDelegates.forEach(
      (d: { id: number; companyId: number }, index: number) => {
        if (d.companyId !== company) {
          this.selectedDelegates.splice(index, 1);
          newDelegates.push(d);
        }
      }
    );
    this.selectedDelegates = newDelegates;
  }
}
